/* Salty Paws - free shipping progress bar + real shipping cost. v2, Aug 26 2026.
   No cart drawer involved. In-flow markup only: no fixed or sticky positioning,
   no overlay, no timer. Reads the real cart via /cart.js.

   v2 change (Aug 26 2026): on the CART PAGE only, the box now also states the
   actual shipping charge - "Shipping: FREE" or "Shipping: $4.75 (added at
   checkout)". Product pages keep the old progress-bar-only behaviour, because
   a dollar figure there would be about the whole cart, not that one product.
   To revert to v1: restore the backup copy of this file.

   ---------------------------------------------------------------------------
   THE ONLY TWO NUMBERS YOU EDIT. Keep them identical to the Shopify rules:
   Settings > Shipping and delivery > General profile > Domestic
     "Free Shipping" = free  when order total is at least $54.99
     "Flat rate"     = $4.75 when order total is $0.00 - $54.98
   Values below are in CENTS. $54.99 = 5499. $4.75 = 475.
   Verified against the live delivery profile on 2026-10-17.

   v3 change (Sep 21 2026): with an EMPTY cart, a product page whose product
   type is "Sweatshirt" says "This sweatshirt ships free." instead of the
   2 tees line. Type is read from /products/<handle>.js. To revert: restore
   sp-ship-bar.BACKUP-2026-09-21.js and change 5800 to 5500.

   v4 change (Oct 10 2026): when the cart is short of free shipping by no more
   than the cheapest item ($34.99 = MIN_ITEM_CENTS), the line says "Add 1 more
   item for free shipping." instead of a dollar amount. The dollar line only
   remains for carts so far short (big discount) that one item would not do it.

   v5 change (Oct 17 2026): THRESHOLD_CENTS 5500 -> 5499 to match the live rule
   (free at $54.99 and up). Before, a $54.99 sale sweatshirt alone showed
   "Add 1 more item" and "$4.75" in the cart while checkout charged nothing.
   Empty cart tee line now matches the announcement bar: "Free shipping on
   2 tees or any sweatshirt." To revert: 5499 -> 5500 and the old line
   "Free shipping when you buy 2 tees."

   v6 change (Oct 22 2026): Linda: "Add 1 more item for free shipping" with
   "Shipping $4.75" right under it read like two answers ("which is it?"). On
   the CART PAGE the two are now fully separate: "Shipping $4.75 added at
   checkout" sits directly under the Estimated total (.sp-ship-cost, outside
   the box), and the box holds only "Add 1 more item for free shipping." and
   the bar. Free carts: no shipping row, box says "Free shipping unlocked. No
   shipping charge at checkout." (the old "Shipping FREE added at checkout"
   row is gone). Product pages unchanged. To revert: restore
   BACKUP_sp-ship-bar.v5_2026-10-22.js from WEBSITE\theme-backups.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var THRESHOLD_CENTS = 5499;
  var FLAT_CENTS = 475;
  var MIN_ITEM_CENTS = 3499;

  var STYLE_ID = 'sp-ship-style';
  var CSS =
    '.sp-ship{margin:16px 0;padding:12px 14px;border:1px solid currentColor;border-radius:8px;font-size:.95rem;line-height:1.45}' +
    '.sp-ship__line{margin:0 0 10px}' +
    '.sp-ship__track{height:6px;border-radius:999px;background:currentColor;opacity:.18}' +
    '.sp-ship__track-outer{height:6px;border-radius:999px;overflow:hidden;position:relative;background:transparent}' +
    '.sp-ship__fill{position:absolute;inset:0 auto 0 0;height:6px;border-radius:999px;background:currentColor;transition:width .3s ease}' +
    '.sp-ship__cost{display:flex;align-items:baseline;justify-content:space-between;gap:8px;margin:10px 0 0;padding-top:10px;border-top:1px solid currentColor}' +
    '.sp-ship__cost-note{display:block;font-size:.8rem;opacity:.7;font-weight:400}' +
    '.sp-ship-cost{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font-size:14px;margin:4px 0 0}';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function money(cents, currency) {
    var amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  /* Build (or find) the bar element inside a given parent, at a given position. */
  function slot(parent, before) {
    if (!parent) return null;
    var el = parent.querySelector(':scope > .sp-ship');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'sp-ship';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    if (before && before.parentNode === parent) {
      parent.insertBefore(el, before);
    } else {
      parent.insertBefore(el, parent.firstChild);
    }
    return el;
  }

  function paint(el, cart, showCost) {
    if (!el) return;
    var total = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    var currency = (cart && cart.currency) || 'USD';
    var remaining = THRESHOLD_CENTS - total;
    var pct = THRESHOLD_CENTS > 0 ? Math.min(100, Math.round((total / THRESHOLD_CENTS) * 100)) : 100;
    var line;

    if (total <= 0 && pageType === 'sweatshirt') {
      line = '<strong>This sweatshirt ships free.</strong>';
      pct = 0;
    } else if (total <= 0) {
      /* Empty cart - only ever seen on a product page, since the cart summary
         does not render when the cart is empty. Worded to match the announcement
         bar exactly: "Free shipping on 2 tees or any sweatshirt." */
      line = '<strong>Free shipping on 2 tees or any sweatshirt.</strong>';
      pct = 0;
    } else if (remaining > 0 && remaining <= MIN_ITEM_CENTS) {
      line = '<strong>Add 1 more item for free shipping.</strong>';
    } else if (remaining > 0) {
      line =
        "You're <strong>" + money(remaining, currency) + '</strong> away from free shipping.';
    } else {
      line = '<strong>Free shipping unlocked.</strong> No shipping charge at checkout.';
      pct = 100;
    }

    var html =
      '<p class="sp-ship__line">' + line + '</p>' +
      '<div class="sp-ship__track-outer"><div class="sp-ship__track"></div>' +
      '<div class="sp-ship__fill" style="width:' + pct + '%"></div></div>';

    el.innerHTML = html;
  }

  /* Every place the bar should appear. Cart page summary + each product form.
     Deliberately does NOT touch the cart drawer.

     CART PAGE: insert inside .cart__summary-inner (a flex column), directly above
     the CHECKOUT button. Do NOT use .cart-page__summary as the parent -- it is a
     grid with `grid-template-rows: subgrid` whose only real child spans
     `grid-row: 1 / -1`, so any extra child lands in row 1 and the checkout button
     draws straight over it. That was the Aug 20 overlap bug on desktop and iPad. */
  function targets() {
    var out = [];
    var inner = document.querySelector('.cart__summary-inner');
    if (inner) {
      out.push({
        parent: inner,
        before: inner.querySelector(':scope > .cart__ctas'),
        cost: true
      });
    }

    var forms = document.querySelectorAll('product-form-component');
    for (var i = 0; i < forms.length; i++) {
      var anchor =
        forms[i].querySelector('[ref="addToCartButtonContainer"]') ||
        forms[i].querySelector('add-to-cart-component');
      if (anchor && anchor.parentNode) {
        out.push({ parent: anchor.parentNode, before: anchor, cost: false });
      }
    }
    return out;
  }

  /* Cart page only, while shipping is still charged: "Shipping $4.75 added at
     checkout" sits directly under the Estimated total, OUTSIDE the box (v6,
     Linda). Removed again once the cart ships free. cart.total_price is after
     any discount, which is exactly what the Shopify rate rule is measured against. */
  function placeCost(cart) {
    var row = document.querySelector('.cart__total-container .cart__total');
    var el = document.querySelector('.sp-ship-cost');
    var total = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    var charged = total > 0 && total < THRESHOLD_CENTS;
    if (!row || !charged) {
      if (el) el.parentNode.removeChild(el);
      return;
    }
    if (!el || el.previousElementSibling !== row) {
      if (el) el.parentNode.removeChild(el);
      el = document.createElement('div');
      el.className = 'sp-ship-cost cart__summary-item';
      row.parentNode.insertBefore(el, row.nextSibling);
    }
    el.innerHTML =
      '<span>Shipping</span>' +
      '<span><strong>' + money(FLAT_CENTS, (cart && cart.currency) || 'USD') +
      '</strong> added at checkout</span>';
  }

  function render(cart) {
    injectStyle();
    var list = targets();
    for (var i = 0; i < list.length; i++) {
      paint(slot(list[i].parent, list[i].before), cart, list[i].cost);
    }
    placeCost(cart);
  }

  /* Product type of the page's own product, lowercased. '' when not a
     product page or when the lookup fails, which falls back to the tees line. */
  var pageType = '';
  var typeReady = null;
  function loadPageType() {
    if (typeReady) return typeReady;
    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) {
      typeReady = Promise.resolve('');
    } else {
      typeReady = fetch('/products/' + m[1] + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (p) { pageType = String((p && p.type) || '').toLowerCase(); return pageType; })
        .catch(function () { return ''; });
    }
    return typeReady;
  }

  var pending = null;
  function refresh() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      loadPageType()
        .then(function () {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } });
        })
        .then(function (r) { return r.json(); })
        .then(function (cart) { render(cart); })
        .catch(function () { /* leave whatever is on screen */ });
    }, 60);
  }

  function boot() {
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Adding to cart from a product page or the quick-add dialog. */
  document.addEventListener('cart:update', refresh);

  /* Quantity changes and line removals on the cart page. */
  document.addEventListener('change', function (e) {
    var n = e.target;
    if (n && typeof n.closest === 'function' && n.closest('cart-items-component')) refresh();
  });
  document.addEventListener('click', function (e) {
    var n = e.target;
    if (n && typeof n.closest === 'function' && n.closest('cart-items-component')) {
      setTimeout(refresh, 600);
    }
  });

  /* Back/forward cache. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
})();